import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CurrencyJpy, Package, Recycle } from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi } from './devApi';
import { jstDateTime } from '@/utils/jstDate';

const yen = (v) => `¥${Math.round(v || 0).toLocaleString('ja-JP')}`;

const Card = ({ title, hint, icon: Icon, children, testid }) => (
  <section className="rounded-lg border border-zinc-300 bg-white p-4 shadow-sm" data-testid={testid}>
    <p className="text-sm font-bold text-zinc-900">
      {Icon && <Icon size={14} className="mr-1 inline text-teal-700" />}{title}
    </p>
    {hint && <p className="text-xs text-zinc-600">{hint}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const Tile = ({ label, value, hint, tone = 'text-zinc-900', testid }) => (
  <div className="rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-sm" data-testid={testid}>
    <p className="text-xs font-semibold text-zinc-600">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
    {hint && <p className="text-xs text-zinc-500">{hint}</p>}
  </div>
);

/** 原価分析 — 種類別・部品別・月次で、どこにお金がかかっているかを見る。 */
export default function CostAnalytics() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    devApi.costAnalytics(12).then(setD).catch(() => toast.error('原価分析を読み込めませんでした'));
  }, []);
  useEffect(() => { if (barRef.current) barRef.current.scrollLeft = barRef.current.scrollWidth; }, [d]);

  if (!d) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-cost-loading">読み込み中…</div>;
  }
  const t = d.totals;
  const maxMonth = Math.max(...d.monthly.map((m) => m.material + m.prototype), 1);
  const maxPart = Math.max(...d.by_part.map((p) => p.amount), 1);

  return (
    <div className="space-y-6" data-testid="dev-cost-page">
      <PageHeader title="原価分析"
        description="開発にかかったお金を、種類別・部品別・月次で横断的に見ます。個別案件を開かずに判断できます。"
        testid="dev-cost-header"
        secondary={
          <>
            <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments/learning')}
              data-testid="dev-cost-learning-btn">学びのダイジェスト</Button>
            <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments/analytics')}
              data-testid="dev-cost-analytics-btn">全体傾向</Button>
          </>
        } />

      {t.projects === 0 ? (
        <EmptyState icon={CurrencyJpy} title="集計できる案件がありません"
          description="部品計画と試作投入が記録されると、ここに原価が表示されます。"
          testid="dev-cost-empty" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="材料費（計画）" value={yen(t.material)} hint={`${t.projects}案件`}
              testid="dev-cost-tile-material" />
            <Tile label="試作費（実投入）" value={yen(t.prototype)} testid="dev-cost-tile-prototype" />
            <Tile label="スクラップ費" value={yen(t.scrap)}
              tone={t.scrap > 0 ? 'text-amber-700' : 'text-zinc-900'}
              hint={t.prototype ? `試作費の ${Math.round((t.scrap / t.prototype) * 100)}%` : null}
              testid="dev-cost-tile-scrap" />
            <Tile label="最終原価" value={yen(t.final)}
              hint={`想定コスト記録あり ${t.with_estimate}件`} testid="dev-cost-tile-final" />
          </div>

          <Card title="月次の推移" hint="材料費（計画）と試作費（実投入）、スクラップ費の内訳"
            testid="dev-cost-monthly">
            <div ref={barRef} className="flex items-end gap-1 overflow-x-auto">
              {d.monthly.map((m) => (
                <div key={m.month} className="flex min-w-[52px] flex-1 flex-col items-center gap-1"
                  data-testid={`dev-cost-month-${m.month}`}>
                  <div className="flex h-32 w-full flex-col justify-end gap-0.5">
                    <div className="w-full rounded-t bg-teal-600"
                      style={{ height: `${(m.prototype / maxMonth) * 100}%` }}
                      title={`試作費 ${yen(m.prototype)}`} />
                    <div className="w-full bg-zinc-400"
                      style={{ height: `${(m.material / maxMonth) * 100}%` }}
                      title={`材料費 ${yen(m.material)}`} />
                    <div className="w-full bg-amber-500"
                      style={{ height: `${(m.scrap / maxMonth) * 100}%` }}
                      title={`スクラップ ${yen(m.scrap)}`} />
                  </div>
                  <p className="text-[10px] tabular-nums text-zinc-600">{m.month.slice(5)}月</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-zinc-600">
              <span className="mr-2"><span className="mr-1 inline-block h-2 w-2 rounded bg-teal-600" />試作費</span>
              <span className="mr-2"><span className="mr-1 inline-block h-2 w-2 rounded bg-zinc-400" />材料費</span>
              <span><span className="mr-1 inline-block h-2 w-2 rounded bg-amber-500" />スクラップ</span>
            </p>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card title="種類別の原価" hint="どの種類の開発にお金がかかっているか"
              testid="dev-cost-types">
              <div className="divide-y divide-zinc-200">
                {d.by_type.map((r) => (
                  <div key={r.label} className="py-2" data-testid={`dev-cost-type-${r.label}`}>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900">{r.label}</p>
                      <p className="shrink-0 text-sm font-bold tabular-nums">{yen(r.final)}</p>
                    </div>
                    <p className="text-xs text-zinc-600">
                      {r.count}件（完了 {r.completed}） · 平均 {yen(r.avg_final)} · 材料 {yen(r.material)}
                      {r.scrap ? ` · スクラップ ${yen(r.scrap)}` : ''}
                      {r.scrap_ratio ? `（試作費の ${r.scrap_ratio}%）` : ''}
                    </p>
                    {r.estimates > 0 && (
                      <p className={`text-xs ${r.variance > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        想定コストより{r.variance > 0 ? '高い' : '安い'} {yen(Math.abs(r.variance))}
                        <span className="text-zinc-600">（{r.estimates}件で比較）</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <Card title="部品別の原価" hint="全案件で投入した部品の金額（上位）" icon={Package}
              testid="dev-cost-parts">
              <div className="space-y-1.5">
                {d.by_part.map((p) => (
                  <div key={p.part_name} data-testid={`dev-cost-part-${p.part_name}`}>
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate text-zinc-900">{p.part_name}</span>
                      <span className="shrink-0 font-bold tabular-nums">{yen(p.amount)}</span>
                    </div>
                    <div className="h-1.5 w-full rounded bg-zinc-200">
                      <div className="h-1.5 rounded bg-teal-600"
                        style={{ width: `${Math.max((p.amount / maxPart) * 100, 3)}%` }} />
                    </div>
                    <p className="text-xs text-zinc-600">
                      投入 {p.installed_qty} / {p.project_count}案件
                      {p.unit_cost ? ` · 単価 ${yen(p.unit_cost)}` : ' · 単価未登録'}
                      {p.scrap_amount > 0
                        ? ` · 廃棄 ${yen(p.scrap_amount)}（${p.scrap_ratio}%）` : ''}
                    </p>
                  </div>
                ))}
                {d.by_part.length === 0 && <p className="text-sm text-zinc-500">投入の記録はありません。</p>}
              </div>
            </Card>
          </div>

          <Card title="案件別の原価" hint="クリックで案件を開きます" icon={Recycle}
            testid="dev-cost-projects">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-zinc-300 text-left text-xs text-zinc-600">
                    <th className="py-2 pr-3 font-semibold">案件</th>
                    <th className="py-2 pr-3 font-semibold">ステージ</th>
                    <th className="py-2 pr-3 text-right font-semibold">材料費</th>
                    <th className="py-2 pr-3 text-right font-semibold">試作費</th>
                    <th className="py-2 pr-3 text-right font-semibold">スクラップ</th>
                    <th className="py-2 pr-3 text-right font-semibold">想定</th>
                    <th className="py-2 text-right font-semibold">最終原価</th>
                  </tr>
                </thead>
                <tbody>
                  {d.projects.map((r) => (
                    <tr key={r.development_id}
                      onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                      data-testid={`dev-cost-row-${r.development_id}`}
                      className="cursor-pointer border-b border-zinc-100 hover:bg-teal-50">
                      <td className="py-2 pr-3">
                        <p className="max-w-[240px] truncate font-semibold text-zinc-900">{r.label}</p>
                        <p className="text-xs text-zinc-600">{r.type_label}</p>
                      </td>
                      <td className="py-2 pr-3 text-xs text-zinc-700">{r.stage}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{yen(r.material)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{yen(r.prototype)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.scrap ? <span className="text-amber-800">{yen(r.scrap)}</span> : '—'}
                      </td>
                      <td className="py-2 pr-3 text-right text-xs tabular-nums text-zinc-600">
                        {r.estimated === null ? '—' : yen(r.estimated)}
                        {r.variance !== null && r.variance > 0 && (
                          <span className="ml-1 font-bold text-rose-700">+{yen(r.variance)}</span>
                        )}
                      </td>
                      <td className="py-2 text-right font-bold tabular-nums">{yen(r.final)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
      <p className="text-xs text-zinc-500">作成: {jstDateTime(d.generated_at)}</p>
    </div>
  );
}
